import type { BriefingDueSession } from '../../api/types'
import { useUnits } from '../../lib/UnitsProvider'
import { Panel, TOKENS } from '../ui/dashboard-primitives'

/**
 * The prescription, sitting beside the report rather than buried a screen away.
 * Rendered on the dark panel variant so it reads as a different kind of thing
 * from the week panel above it.
 *
 * The session carries its own lifts, so this component fetches nothing and
 * matches nothing: it renders what the briefing sent.
 */
export function UpNextCard({ session }: { session: BriefingDueSession }) {
  const { formatLoad } = useUnits()
  const overdue = session.never_trained
    ? 'New'
    : session.days_since != null
      ? `${session.days_since} ${session.days_since === 1 ? 'day' : 'days'} since last`
      : null

  return (
    <Panel variant="dark" className="mt-10">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <span
          className="text-[0.5625rem] tracking-[0.28em] uppercase"
          style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
        >
          Up next
        </span>
        {overdue && (
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase border whitespace-nowrap"
            style={{
              fontFamily: 'var(--font-mono)',
              backgroundColor: TOKENS.terracottaSoft,
              color: TOKENS.terracotta,
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              borderColor: 'rgba(198,110,77,0.32)',
            }}
          >
            {overdue}
          </span>
        )}
      </div>

      <h3
        className="mb-1.5"
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: '1.375rem',
          letterSpacing: '-0.02em',
          color: TOKENS.cream,
        }}
      >
        {session.name}
      </h3>
      <p
        className="mb-4 text-sm"
        style={{ color: TOKENS.creamSoft, maxWidth: '40ch' }}
      >
        {session.exercise_count} {session.exercise_count === 1 ? 'exercise' : 'exercises'} prescribed.
      </p>

      {session.exercises.length > 0 && (
        <div className="grid">
          {session.exercises.map((lift, i) => (
            <div
              key={`${lift.name}-${i}`}
              className="grid items-baseline gap-4 py-2 text-sm"
              style={{
                gridTemplateColumns: '1fr auto',
                borderTop: i === 0 ? undefined : '1px solid ' + TOKENS.creamHairline,
                color: TOKENS.creamSoft,
              }}
            >
              <span className="truncate">{lift.name}</span>
              <b
                className="tabular-nums"
                style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, color: TOKENS.cream }}
              >
                {lift.target_weight_kg != null ? formatLoad(lift.target_weight_kg) : '·'}
              </b>
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}
